/**
 * UX для шести однознаковых полей кода 2FA.
 *
 * Разметку отдаёт бэкенд: message_totp_enable(), message_totp_disable() и
 * message_totp_check() в engine/function/auth/func.php. Модалка прилетает в
 * content_html и вставляется в body из handleResponse (helpers/js_form.js),
 * поэтому все слушатели делегированы на document, а не навешиваются на поля.
 *
 * Состояние кнопки считает valid/form.js по .input-necessarily, здесь только
 * ввод, фокус и автосабмит.
 */

const FIELD_SELECTOR = 'input[name="totp_code_parts[]"]';
const GROUP_SELECTOR = '.otp-field-wrapper';
const FORM_SELECTOR = '.form_for_js_in_div';
const BUTTON_SELECTOR = '.button_for_js_in_div.necessarily-impacted';
const BACKUP_CODES_SELECTOR = '.form-2fa--reserve-codes';
const DONE_SELECTOR = '[data-totp-done="1"]';

export const extractDigits = (text, maxLength) =>
    String(text ?? '').replace(/\D/g, '').slice(0, Math.max(0, maxLength));

export const distributeDigits = (values, startIndex, digits) => {
    const next = [...values];
    for (let i = 0; i < digits.length && startIndex + i < next.length; i++) {
        next[startIndex + i] = digits[i];
    }
    return next;
};

export const focusIndexAfterFill = (values) => {
    const emptyIndex = values.indexOf('');
    return emptyIndex === -1 ? Math.max(0, values.length - 1) : emptyIndex;
};

export const planBackspace = (values, index) => {
    const next = [...values];
    if (next[index]) {
        next[index] = '';
        return {values: next, focusIndex: index};
    }
    const previous = index - 1;
    if (previous < 0) {
        return {values: next, focusIndex: 0};
    }
    next[previous] = '';
    return {values: next, focusIndex: previous};
};

// Пока мы сами правим value и рассылаем input, наши же обработчики должны молчать,
// иначе получится рекурсия. valid/form.js при этом события видит и кнопку пересчитывает.
let programmatic = false;

const fieldsOf = (input) => {
    const group = input.closest(GROUP_SELECTOR) || input.closest(FORM_SELECTOR);
    return group ? [...group.querySelectorAll(FIELD_SELECTOR)] : [input];
};

const setFieldValue = (field, value) => {
    if (field.value === value) return;
    programmatic = true;
    field.value = value;
    field.dispatchEvent(new Event('input', {bubbles: true}));
    programmatic = false;
};

const focusField = (field) => {
    if (!field) return;
    field.focus();
    if (typeof field.select === 'function') field.select();
};

//js_form.js держит метку на кнопке, пока её POST не завершился
const isRequestPending = (form) => {
    const button = form.querySelector(BUTTON_SELECTOR);
    return !!button && button.dataset.requestPending === '1';
};

const submitWhenComplete = (fields) => {
    if (fields.some(field => field.disabled)) return;
    const form = fields.length ? fields[0].closest(FORM_SELECTOR) : null;
    if (!form) return;

    if (fields.some(field => field.value === '')) {
        delete form.dataset.otpSubmitted;
        return;
    }
    // js_form.js держит data-request-pending до конца POST. Без этой проверки
    // Backspace плюс новая цифра отправляли второй код параллельно первому:
    // valid/form.js успевает снять disabled с кнопки прямо посреди запроса.
    if (isRequestPending(form)) return;
    if (form.dataset.otpSubmitted === '1') return;
    form.dataset.otpSubmitted = '1';

    // valid/form.js снимает disabled в своём слушателе input, поэтому кликаем следующим тиком
    setTimeout(() => {
        const button = form.querySelector(BUTTON_SELECTOR);
        if (fields.some(field => field.disabled) || isRequestPending(form) || !button || button.disabled) {
            // No autoclick happened. Allow a later input to try again.
            delete form.dataset.otpSubmitted;
            return;
        }
        button.click();
    }, 0);
};

const applyDigits = (fields, startIndex, digits) => {
    const values = distributeDigits(fields.map(field => field.value), startIndex, digits);
    fields.forEach((field, i) => setFieldValue(field, values[i]));
    focusField(fields[focusIndexAfterFill(values)]);
    submitWhenComplete(fields);
};

const handleBeforeInput = (e) => {
    const field = e.target.closest?.(FIELD_SELECTOR);
    if (!field || field.disabled || programmatic) return;
    if (e.inputType && e.inputType.startsWith('delete')) return;
    if (typeof e.data === 'string' && e.data !== '' && !/^\d+$/.test(e.data)) {
        e.preventDefault();
    }
};

const handleInput = (e) => {
    const field = e.target.closest?.(FIELD_SELECTOR);
    if (!field || field.disabled || programmatic) return;

    const fields = fieldsOf(field);
    const index = fields.indexOf(field);
    const digits = extractDigits(field.value, fields.length - index);

    // autocomplete="one-time-code" на мобильных кладёт весь код в первое поле
    if (digits.length > 1) {
        applyDigits(fields, index, digits);
        return;
    }

    setFieldValue(field, digits);
    if (digits) focusField(fields[Math.min(index + 1, fields.length - 1)]);
    submitWhenComplete(fields);
};

const handleKeydown = (e) => {
    const field = e.target.closest?.(FIELD_SELECTOR);
    if (!field || field.disabled) return;

    const fields = fieldsOf(field);
    const index = fields.indexOf(field);

    if (e.key === 'Backspace') {
        e.preventDefault();
        const {values, focusIndex} = planBackspace(fields.map(f => f.value), index);
        fields.forEach((f, i) => setFieldValue(f, values[i]));
        focusField(fields[focusIndex]);
        submitWhenComplete(fields);
        return;
    }

    if (e.key === 'ArrowLeft' && index > 0) {
        e.preventDefault();
        focusField(fields[index - 1]);
        return;
    }

    if (e.key === 'ArrowRight' && index < fields.length - 1) {
        e.preventDefault();
        focusField(fields[index + 1]);
    }
};

const handlePaste = (e) => {
    const field = e.target.closest?.(FIELD_SELECTOR);
    if (!field || field.disabled) return;
    e.preventDefault();

    const fields = fieldsOf(field);
    const index = fields.indexOf(field);
    const clipboard = e.clipboardData || window.clipboardData;
    const digits = extractDigits(clipboard && clipboard.getData('text'), fields.length - index);
    if (digits) applyDigits(fields, index, digits);
};

const handleFocus = (e) => {
    const field = e.target.closest?.(FIELD_SELECTOR);
    // maxlength="1" не даёт перезаписать занятое поле, пока символ не выделен
    if (field && typeof field.select === 'function') field.select();
};

const clearFields = (root) => {
    const fields = [...root.querySelectorAll(FIELD_SELECTOR)];
    fields.forEach(field => setFieldValue(field, ''));
    const form = fields.length ? fields[0].closest(FORM_SELECTOR) : null;
    if (form) delete form.dataset.otpSubmitted;
};

const isDoneResponse = (node) =>
    node.matches(DONE_SELECTOR) ||
    node.matches(BACKUP_CODES_SELECTOR) ||
    !!node.querySelector(`${DONE_SELECTOR}, ${BACKUP_CODES_SELECTOR}`);

/**
 * js_form.js шлёт js-form:response на ту самую форму, чей POST завершился,
 * и кладёт в detail вставленный в body ответ. Успех, распознаваемый по
 * data-totp-done или по списку backup-кодов, снимает форму ввода кода. Всё
 * остальное считаем ошибкой: форму оставляем и чистим поля, чтобы код можно
 * было набрать заново тем же секретом.
 *
 * Раньше это делалось по MutationObserver на любую добавленную модалку, из-за
 * чего постороннее уведомление стирало набранный код.
 */
const handleFormResponse = (e) => {
    const form = e.target;
    if (!form || typeof form.querySelector !== 'function') return;
    if (!form.querySelector(FIELD_SELECTOR)) return;

    const responseNodes = e.detail?.responseNodes || [e.detail?.responseNode];
    if (responseNodes.some(node => node && isDoneResponse(node))) {
        form.remove();
        return;
    }
    clearFields(form);
};

const handleAddedNode = (node) => {
    if (node.nodeType !== 1 || typeof node.querySelectorAll !== 'function') return;
    const fields = [...node.querySelectorAll(FIELD_SELECTOR)];
    if (fields.length) focusField(fields[0]);
};

export const otpInput = () => {
    document.addEventListener('beforeinput', handleBeforeInput);
    document.addEventListener('input', handleInput);
    document.addEventListener('keydown', handleKeydown);
    document.addEventListener('paste', handlePaste);
    document.addEventListener('focusin', handleFocus);
    document.addEventListener('js-form:response', handleFormResponse);

    const observer = new MutationObserver(records => {
        records.forEach(record => record.addedNodes.forEach(handleAddedNode));
    });
    observer.observe(document.body, {childList: true});
};
